import { Component, OnInit, OnDestroy, ElementRef, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { SessionService } from 'src/app/shared/services/session.service';

declare let JitsiMeetExternalAPI: any;

@Component({
  selector: 'app-jitsi-meeting',
  templateUrl: './jitsi-meeting.component.html',
  styleUrls: ['./jitsi-meeting.component.scss']
})
export class JitsiMeetingComponent implements OnInit, OnDestroy {
  private api: any;
  private readonly virtualBackgroundAssetUrl = '/assets/images/marsh-virtual-bg.jpeg';
  private virtualBackgroundDataUriPromise: Promise<string> | null = null;

  private userName = '';
  private userEmail = '';

  openMeetingRoom = false;

  @Output() closeMeetingEvent = new EventEmitter<void>();

  private readonly configOverwrite = {
    startWithAudioMuted: false,
    startWithVideoMuted: false,
    notifications: [],
    prejoinConfig: {
      enabled: false
    },
    toolbarButtons: ['microphone', 'camera', 'participants-pane', 'chat', 'select-background'],
    hideConferenceSubject: true,
    hideConferenceTimer: true,
    hideRecordingLabel: true,
    hideParticipantsStats: true,
    conferenceInfo: {
      autoHide: []
    },
    filmstrip: {
      disabled: true
    },
    logging: {
      defaultLogLevel: 'error'
    }
  };

  constructor(
    private readonly elementRef: ElementRef,
    private readonly sessionService: SessionService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const user = this.sessionService.getSession();
    this.userName = user.name;
    this.userEmail = user.emailaddress;
  }

  async startMeeting(meetingDomain: string, meetingRoom: string): Promise<void> {
    this.openMeetingRoom = true;
    this.cdr.detectChanges();

    const domain = meetingDomain?.trim();
    let roomName = meetingRoom?.trim();

    try {
      if (!domain || !roomName) {
        throw new Error('Meeting domain and room are required.');
      }

      roomName = roomName.replace(/^\/+|\/+$/g, '');
      // roomName = this.applyJaasRoomPrefix(domain, roomName);
    } catch (error) {
      this.openMeetingRoom = false;
      this.cdr.detectChanges();
      console.error('Invalid Jitsi meeting data', { meetingDomain, meetingRoom, error });
      return;
    }

    this.api = new JitsiMeetExternalAPI(domain, {
      roomName,
      parentNode: this.elementRef.nativeElement.querySelector('#jaas-container'),
      userInfo: {
        displayName: this.userName,
        email: this.userEmail
      },
      configOverwrite: this.configOverwrite,
      interfaceConfigOverwrite: {
        VIDEO_LAYOUT_FIT: 'nocrop',
        MOBILE_APP_PROMO: false,
        TILE_VIEW_MAX_COLUMNS: 4,
        SHOW_CHROME_EXTENSION_BANNER: false,
        AUTO_JOIN_ROOM: true
      },
      lang: 'pt-BR'
    });

    void this.applyVirtualBackground();

    this.api.addEventListener('videoConferenceJoined', () => {
      void this.applyVirtualBackground();
      this.api.executeCommand('setTileView', true);
    });
  }

  private async validateImage(src: string): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const image = new Image();

      image.onload = (): void => {
        resolve();
      };

      image.onerror = (): void => {
        reject(new Error('Nao foi possivel carregar o virtual background configurado.'));
      };

      image.src = src;
    });
  }

  private async assetUrlToDataUri(assetUrl: string): Promise<string> {
    const response = await fetch(assetUrl);
    if (!response.ok) {
      throw new Error('Nao foi possivel baixar o asset do virtual background.');
    }

    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = (): void => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('Conversao para data URI retornou resultado invalido.'));
          return;
        }

        resolve(result);
      };

      reader.onerror = (): void => {
        reject(new Error('Erro ao converter asset do virtual background em data URI.'));
      };

      reader.readAsDataURL(blob);
    });
  }

  private getVirtualBackgroundDataUri(): Promise<string> {
    if (!this.virtualBackgroundDataUriPromise) {
      this.virtualBackgroundDataUriPromise = this.assetUrlToDataUri(this.virtualBackgroundAssetUrl);
    }

    return this.virtualBackgroundDataUriPromise;
  }

  private async applyVirtualBackground(): Promise<void> {
    if (!this.api) {
      return;
    }

    try {
      const dataUri = await this.getVirtualBackgroundDataUri();
      await this.validateImage(dataUri);
      this.api.executeCommand('setVirtualBackground', true, dataUri);
    } catch (error) {
      console.error('Unable to apply Jitsi virtual background', error);
    }
  }

  ngOnDestroy(): void {
    if (this.api) {
      this.api.dispose();
    }
  }

  close(): void {
    if (this.api) {
      this.openMeetingRoom = false;
      this.closeMeetingEvent.emit();
      this.api.dispose();

      window.location.reload();
    }
  }

  // private applyJaasRoomPrefix(domain: string, roomName: string): string {
  //   const isJaasDomain = domain.toLowerCase().includes('8x8.vc');
  //   const hasRoomPrefix = roomName.includes('/');

  //   if (isJaasDomain && !hasRoomPrefix && this.jitsiAppId) {
  //     return `${this.jitsiAppId}/${roomName}`;
  //   }

  //   return roomName;
  // }
}
